import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_STYLES = {
  pending:  { bg: "bg-yellow-400/10", border: "border-yellow-400/40", text: "text-yellow-300", label: "Pending" },
  approved: { bg: "bg-emerald-400/10",border: "border-emerald-400/40",text: "text-emerald-300",label: "Approved" },
  rejected: { bg: "bg-red-400/10",    border: "border-red-400/40",    text: "text-red-300",    label: "Rejected" },
};

const Stars = ({ value }) => (
  <div className="inline-flex gap-0.5 text-gold">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={12} fill={n <= value ? "currentColor" : "none"} />
    ))}
  </div>
);

const ReviewsAdmin = () => {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = (status) => {
    setLoading(true);
    const q = status === "all" ? "" : `?status=${status}`;
    api.get(`/admin/reviews${q}`)
      .then(({ data }) => setReviews(data.reviews || []))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const act = async (id, action) => {
    const note = action === "reject"
      ? window.prompt("Reason (optional, shown to no one but you):") || ""
      : "";
    setBusy(id);
    try {
      if (action === "delete") {
        if (!window.confirm("Delete this review permanently?")) { setBusy(null); return; }
        await api.delete(`/admin/reviews/${id}`);
        toast.success("Review deleted");
      } else {
        await api.post(`/admin/reviews/${id}/${action}`, { note });
        toast.success(`Review ${action}d`);
      }
      load(filter);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const filterTabs = [
    { k: "pending", l: "Pending" },
    { k: "approved", l: "Approved" },
    { k: "rejected", l: "Rejected" },
    { k: "all", l: "All" },
  ];

  return (
    <div data-testid="reviews-admin" className="space-y-6">
      <div>
        <h2 className="font-heading text-3xl text-ivory">Reviews</h2>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Approve customer reviews to display them on the public product page
          and feed them into Google's rich-snippet schema. Approved reviews
          replace the placeholder rating immediately.
        </p>
      </div>

      <div className="flex gap-2 border-b border-white/10 pb-4 flex-wrap">
        {filterTabs.map((t) => (
          <button
            key={t.k}
            data-testid={`reviews-filter-${t.k}`}
            onClick={() => setFilter(t.k)}
            className={`font-accent text-[10px] tracking-[0.28em] uppercase px-4 py-2.5 ${
              filter === t.k
                ? "text-gold border-b border-gold -mb-[17px] pb-4"
                : "text-white/50 hover:text-white"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="bg-onyx border border-white/10 p-12 text-center text-white/40">
          No reviews in this view.
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => {
            const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
            return (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-onyx border border-white/10 p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div className="flex items-center gap-3">
                    <Stars value={r.rating} />
                    <span className="text-white/40 text-xs">{r.gemstone_name}</span>
                  </div>
                  <span className={`px-2 py-1 border ${s.bg} ${s.border} ${s.text} font-accent text-[9px] tracking-[0.24em] uppercase`}>
                    {s.label}
                  </span>
                </div>
                {r.title && (
                  <h3 className="font-heading text-lg text-ivory mb-1">{r.title}</h3>
                )}
                <p className="text-white/80 text-sm whitespace-pre-line">{r.body}</p>
                <div className="text-xs text-white/40 mt-3">
                  <span className="text-ivory">{r.display_name}</span>{" "}
                  <span className="text-white/30">·</span>{" "}
                  <span>{r.buyer_email}</span>{" "}
                  <span className="text-white/30">·</span>{" "}
                  <span>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                  {r.order_reference && (
                    <>
                      {" "}<span className="text-white/30">·</span>{" "}
                      <span className="font-mono text-gold">{r.order_reference}</span>
                    </>
                  )}
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  {r.status !== "approved" && (
                    <button
                      data-testid={`approve-${r.id}`}
                      onClick={() => act(r.id, "approve")}
                      disabled={busy === r.id}
                      className="font-accent text-[10px] tracking-[0.24em] uppercase border border-emerald-400 text-emerald-300 px-4 py-2 hover:bg-emerald-400 hover:text-black disabled:opacity-50"
                    >
                      Approve
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button
                      data-testid={`reject-${r.id}`}
                      onClick={() => act(r.id, "reject")}
                      disabled={busy === r.id}
                      className="font-accent text-[10px] tracking-[0.24em] uppercase border border-red-400/60 text-red-300 px-4 py-2 hover:bg-red-400 hover:text-black disabled:opacity-50"
                    >
                      Reject
                    </button>
                  )}
                  <button
                    data-testid={`delete-${r.id}`}
                    onClick={() => act(r.id, "delete")}
                    disabled={busy === r.id}
                    className="font-accent text-[10px] tracking-[0.24em] uppercase border border-white/20 px-4 py-2 text-white/50 hover:text-white disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewsAdmin;
